import useSWR from 'swr'

// Widget API base URL (set by widget init for cross-origin requests)
let widgetApiBaseUrl: string | null = null

export function setWidgetApiBaseUrl(url: string) {
  widgetApiBaseUrl = url.replace(/\/$/, '') // Remove trailing slash
}

export function getApiBaseUrl(): string {
  return widgetApiBaseUrl || ''
}

// Types matching the optimized API response
export interface ConfigProduct {
  id: string
  name: string
  sku: string
  wcId?: number
  price: number
  regularPrice?: number // Original price when on sale
  isOnSale?: boolean
  thumb: string
  image: string
  layer: number
  previewImage?: string
  tags: string[]
  inStock: boolean
}

export interface ColorFilter {
  id: string
  label: string
  hex?: string
  image?: string
  tag: string
}

export interface ConfigCategory {
  id: string
  name: string
  required: boolean
  displayMode: 'grid' | 'thumbs' | 'list'
  products: ConfigProduct[]
}

export interface ConfigStep {
  type: 'category' | 'question'
  categoryId?: string
  id?: string
  text?: string
  optionNo?: { label: string; showCategories: string[] }
  optionYes?: { label: string; showCategories: string[] }
}

export interface BundleSlotConfig {
  id: string
  name: string
  sourceCategoryId: string
  defaultProductId: string
  swapsLayerPosition?: number
}

export interface BundleConfigData {
  discountPercent: number
  description?: string
  baseLayerPicks: Record<string, string>
  slots: BundleSlotConfig[]
}

export interface FunnelConfig {
  id: string
  name: string
  slug: string
  size: 's' | 'm' | 'l'
  type: 'configurator' | 'bundle'
  baseImage: string
  baseName?: string
  basePrice: number
  baseWcId?: number | null
  categories: ConfigCategory[]
  configSteps: ConfigStep[]
  colorFilterTitle?: string
  colorFilters?: ColorFilter[]
  funnelUrls?: Record<string, string>
  dimensions?: string
  info?: string
  bundleConfig?: BundleConfigData
  ts: number
}

const fetcher = async (url: string): Promise<FunnelConfig> => {
  // Use widget API base URL if set (for cross-origin widget usage)
  const fullUrl = widgetApiBaseUrl ? `${widgetApiBaseUrl}${url}` : url
  const res = await fetch(fullUrl)
  if (!res.ok) throw new Error('Funnel not found')
  return res.json()
}

interface UseFunnelConfigOptions {
  // Prefetched data for SSR/SSG
  fallbackData?: FunnelConfig
  // Revalidate on focus (default: false for perf)
  revalidateOnFocus?: boolean
}

export function useFunnelConfig(slug: string, options: UseFunnelConfigOptions = {}) {
  const { data, error, isLoading, mutate } = useSWR<FunnelConfig>(
    slug ? `/api/funnel/${slug}` : null,
    fetcher,
    {
      fallbackData: options.fallbackData,
      revalidateOnFocus: options.revalidateOnFocus ?? true,
      revalidateOnReconnect: true,
      dedupingInterval: 5000, // Match the CDN cache window so stock updates propagate quickly
      keepPreviousData: true,
    }
  )

  return {
    config: data,
    isLoading,
    isError: !!error,
    error,
    refresh: mutate,
  }
}

// Preload function for faster initial load
export function preloadFunnelConfig(slug: string) {
  if (typeof window === 'undefined') return

  // Use link preload for the API
  const link = document.createElement('link')
  link.rel = 'preload'
  link.as = 'fetch'
  link.href = `/api/funnel/${slug}`
  link.crossOrigin = 'anonymous'
  document.head.appendChild(link)
}

// Preload images for a category
export function preloadCategoryImages(products: ConfigProduct[]) {
  if (typeof window === 'undefined') return

  products.forEach(p => {
    if (p.image) {
      const img = new Image()
      img.src = p.image
    }
  })
}
